import { createSlice } from '@reduxjs/toolkit';
// Helper function to reorder array items
const reorderArray = (list, startIndex, endIndex) => {
    const result = Array.from(list);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);
    return result;
};
// Initial inputs state
export const initialWallBuilderInputsState = {
    wallType: null,
    zoom: 10,
    segments: [],
    selectedBlock: null,
    viewMode: 'front',
    selectedTopStoneId: '',
    selectedEndStoneId: '',
    selectedTopCornerStoneId: '',
    isAddingToCart: false,
};
// Create Redux slice for inputs
const wallBuilderInputsSlice = createSlice({
    name: 'wallBuilderInputs',
    initialState: initialWallBuilderInputsState,
    reducers: {
        setWallType: (state, action) => {
            state.wallType = action.payload;
        },
        setZoom: (state, action) => {
            state.zoom = action.payload;
        },
        setSelectedBlock: (state, action) => {
            var _a, _b, _c, _d, _e, _f, _g;
            // When block changes, update global stone selections and segments
            const newBlock = action.payload;
            // Handle default_top_stone which might be ProductData or ProductWithExtraProducts
            let defaultTopStone = '';
            if (newBlock.default_top_stone) {
                if ('variant' in newBlock.default_top_stone) {
                    // It's ProductWithExtraProducts
                    defaultTopStone = ((_a = newBlock.default_top_stone.variant.variant_id) === null || _a === void 0 ? void 0 : _a.toString()) || '';
                }
                else {
                    // It might be just ProductData
                    const defaultStone = newBlock.default_top_stone;
                    defaultTopStone = ((_b = defaultStone.variant_id) === null || _b === void 0 ? void 0 : _b.toString()) || '';
                }
            }
            const defaultEndStone = ((_e = (_d = (_c = newBlock.end_stones) === null || _c === void 0 ? void 0 : _c[0]) === null || _d === void 0 ? void 0 : _d.variant_id) === null || _e === void 0 ? void 0 : _e.toString()) || '';
            const defaultTopCornerStone = ((_g = (_f = newBlock.corner_top) === null || _f === void 0 ? void 0 : _f.variant.variant_id) === null || _g === void 0 ? void 0 : _g.toString()) || '';
            state.selectedBlock = newBlock;
            state.selectedTopStoneId = defaultTopStone;
            state.selectedEndStoneId = defaultEndStone;
            state.selectedTopCornerStoneId = defaultTopCornerStone;
            state.segments = state.segments.map((segment) => (Object.assign(Object.assign({}, segment), { selectedTopStoneId: defaultTopStone, selectedEndStoneId: defaultEndStone, selectedTopCornerStoneId: defaultTopCornerStone, topStone: Boolean(newBlock.top_stones && newBlock.top_stones.length > 0), startingStone: Boolean(segment.startingStone && newBlock.end_stones && newBlock.end_stones.length > 0), endStone: Boolean(segment.endStone && newBlock.end_stones && newBlock.end_stones.length > 0), topCornerStone: Boolean(segment.topCornerStone && newBlock.corner_top) })));
        },
        setViewMode: (state, action) => {
            state.viewMode = action.payload;
        },
        setGlobalStoneIds: (state, action) => {
            const { topStoneId, endStoneId, topCornerStoneId } = action.payload;
            if (topStoneId !== undefined)
                state.selectedTopStoneId = (topStoneId === null || topStoneId === void 0 ? void 0 : topStoneId.toString()) || '';
            if (endStoneId !== undefined)
                state.selectedEndStoneId = (endStoneId === null || endStoneId === void 0 ? void 0 : endStoneId.toString()) || '';
            if (topCornerStoneId !== undefined)
                state.selectedTopCornerStoneId = (topCornerStoneId === null || topCornerStoneId === void 0 ? void 0 : topCornerStoneId.toString()) || '';
        },
        setIsAddingToCart: (state, action) => {
            state.isAddingToCart = action.payload;
        },
        addSegment: (state, action) => {
            state.segments.push(action.payload);
        },
        removeSegment: (state, action) => {
            const filteredSegments = state.segments.filter((segment) => segment.id !== action.payload);
            // Clear corner stones from the last segment if it has any
            if (filteredSegments.length > 0) {
                const lastIndex = filteredSegments.length - 1;
                if (filteredSegments[lastIndex].cornerStones > 0) {
                    filteredSegments[lastIndex].cornerStones = 0;
                }
            }
            state.segments = filteredSegments;
        },
        updateSegment: (state, action) => {
            const { segmentId, field, value } = action.payload;
            const segmentIndex = state.segments.findIndex((segment) => segment.id === segmentId);
            if (segmentIndex !== -1) {
                state.segments[segmentIndex][field] = value;
            }
        },
        updateSegmentName: (state, action) => {
            const { segmentId, name } = action.payload;
            const segmentIndex = state.segments.findIndex((segment) => segment.id === segmentId);
            if (segmentIndex !== -1) {
                state.segments[segmentIndex].name = name.trim() || `Segment ${segmentIndex + 1}`;
            }
        },
        updateSegmentCornerStones: (state, action) => {
            const { segmentId, hasCornerStones } = action.payload;
            const segmentIndex = state.segments.findIndex((segment) => segment.id === segmentId);
            if (segmentIndex !== -1) {
                state.segments[segmentIndex].cornerStones = hasCornerStones ? 1 : 0;
            }
        },
        updateSegmentTopStone: (state, action) => {
            const { segmentId, topStone } = action.payload;
            const segmentIndex = state.segments.findIndex((segment) => segment.id === segmentId);
            if (segmentIndex !== -1) {
                state.segments[segmentIndex].topStone = topStone;
            }
        },
        updateSegmentStartingStone: (state, action) => {
            const { segmentId, startingStone } = action.payload;
            const segmentIndex = state.segments.findIndex((segment) => segment.id === segmentId);
            if (segmentIndex !== -1) {
                state.segments[segmentIndex].startingStone = startingStone;
            }
        },
        updateSegmentEndStone: (state, action) => {
            const { segmentId, endStone } = action.payload;
            const segmentIndex = state.segments.findIndex((segment) => segment.id === segmentId);
            if (segmentIndex !== -1) {
                state.segments[segmentIndex].endStone = endStone;
            }
        },
        updateSegmentTopCornerStone: (state, action) => {
            const { segmentId, topCornerStone, selectedTopCornerStoneId } = action.payload;
            const segmentIndex = state.segments.findIndex((segment) => segment.id === segmentId);
            if (segmentIndex !== -1) {
                state.segments[segmentIndex].topCornerStone = topCornerStone;
                state.segments[segmentIndex].selectedTopCornerStoneId = topCornerStone
                    ? selectedTopCornerStoneId || state.selectedTopCornerStoneId
                    : undefined;
            }
        },
        reorderSegments: (state, action) => {
            const { startIndex, endIndex } = action.payload;
            state.segments = reorderArray(state.segments, startIndex, endIndex);
        },
        resetSegments: (state) => {
            state.segments = [];
        },
        resetAll: (state) => {
            Object.assign(state, initialWallBuilderInputsState);
        },
    },
});
// Export actions
export const wallBuilderInputsActions = wallBuilderInputsSlice.actions;
// Export reducer
export const wallBuilderInputsReducer = wallBuilderInputsSlice.reducer;
// Export slice for injection
export { wallBuilderInputsSlice };
